import { Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Box from "@mui/material/Box";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import { t } from "i18next";
import React, { useEffect, useState } from "react";

import { formatHoursMinutes } from "@repo/legacy/components/helper/Index";
import { useProjectProgressData } from "../../hooks/useProjectProgressData";

import type { ProjectTimeSummary } from "@repo/server-api";

export default function ProjectProgressPieBox({
	projectTimeSummaries,
}: {
	projectTimeSummaries: ProjectTimeSummary[];
}): React.ReactElement {
	const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
	const [allPositions, setAllPositions] = useState<
		{ id: string; position: string; project: string }[]
	>([]);

	// Use the custom hook for all data processing
	const { positionSummaries, statusData, positionStatusData, totals } = useProjectProgressData(
		projectTimeSummaries,
		selectedPositions,
	);

	// Initialize selected positions when data changes
	useEffect(() => {
		if (!positionSummaries.length) return;

		const positions = positionSummaries.map((s) => ({
			id: s.id,
			position: s.positionName,
			project: s.projectName,
		}));

		setAllPositions(positions);
		setSelectedPositions(positions.map((p) => p.id));
	}, [positionSummaries]);

	const innerRadius = 0;
	const middleRadius = 90;

	return (
		<Box sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%", overflow: "hidden" }}>
			{/* Linke Seite: Tabelle */}
			<Box sx={{ flex: "1 1 auto", minWidth: 0, overflow: "auto" }}>
				<Box>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell padding="checkbox" />
								<TableCell sx={{ fontWeight: "bold" }}>{t("position")}</TableCell>
								<TableCell align="right" sx={{ fontWeight: "bold", color: "#4caf50" }}>
									{t("approved")}
								</TableCell>
								<TableCell align="right" sx={{ fontWeight: "bold", color: "#f9a825" }}>
									{t("pending")}
								</TableCell>
								<TableCell align="right" sx={{ fontWeight: "bold", color: "#2196f3" }}>
									{t("open")}
								</TableCell>
								<TableCell align="right" sx={{ fontWeight: "bold", color: "#9e9e9e" }}>
									{t("left")}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{positionSummaries.map((summary) => (
								<TableRow
									key={summary.id}
									hover
									sx={{ "&:hover": { backgroundColor: "action.hover" } }}>
									<TableCell padding="checkbox">
										<Checkbox
											checked={selectedPositions.includes(summary.id)}
											onChange={(event) => {
												if (event.target.checked) {
													setSelectedPositions((prev) => [...prev, summary.id]);
												} else {
													setSelectedPositions((prev) => prev.filter((pos) => pos !== summary.id));
												}
											}}
											size="small"
										/>
									</TableCell>
									<TableCell sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
										{summary.positionName}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: "0.875rem" }}>
										{formatHoursMinutes(summary.statusMinutes.approved)}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: "0.875rem" }}>
										{formatHoursMinutes(summary.statusMinutes.pending)}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: "0.875rem" }}>
										{formatHoursMinutes(summary.statusMinutes.open)}
									</TableCell>
									<TableCell align="right" sx={{ fontSize: "0.875rem" }}>
										{formatHoursMinutes(summary.leftMinutes)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Box>

			{/* Rechte Seite: PieChart */}
			<Box sx={{ flex: "0 0 auto" }}>
				<PieChart
					width={350}
					height={250}
					margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
					slots={{
						legend: () => null,
					}}
					series={[
						{
							innerRadius,
							outerRadius: middleRadius,
							data: statusData,
							arcLabel:
								selectedPositions.length > 1
									? undefined
									: (item) => {
											const percentage =
												totals.totalMinutes > 0 ? (item.value / totals.totalMinutes) * 100 : 0;
											return percentage === 0 ? "" : `${item.label} (${percentage.toFixed(0)}%)`;
										},
							arcLabelMinAngle: 15,
							valueFormatter: ({ value }) =>
								t("time-out-of-total", {
									value: formatHoursMinutes(value),
									total: formatHoursMinutes(totals.totalMinutes),
									percentage: totals.totalMinutes
										? ((value / totals.totalMinutes) * 100).toFixed(0)
										: 0,
								}),
							highlightScope: { fade: "global", highlight: "item" },
							highlighted: { additionalRadius: 2 },
							cornerRadius: 3,
						},
						{
							innerRadius: middleRadius,
							outerRadius: middleRadius + 30,
							data: positionStatusData,
							arcLabel:
								selectedPositions.length > 1
									? undefined
									: (item) => {
											const percentage =
												totals.totalMinutes > 0 ? (item.value / totals.totalMinutes) * 100 : 0;
											return percentage === 0 ? "" : `${item.label} (${percentage.toFixed(0)}%)`;
										},
							arcLabelMinAngle: 20,
							arcLabelRadius: 120,
							valueFormatter: ({ value }) =>
								t("time-out-of-total", {
									value: formatHoursMinutes(value),
									total: formatHoursMinutes(totals.totalMinutes),
									percentage: totals.totalMinutes
										? ((value / totals.totalMinutes) * 100).toFixed(0)
										: 0,
								}),
							highlightScope: { fade: "global", highlight: "item" },
							highlighted: { additionalRadius: 2 },
							cornerRadius: 3,
						},
					]}
					sx={{
						[`& .${pieArcLabelClasses.root}`]: {
							fontSize: "12px",
						},
					}}
				/>
			</Box>
		</Box>
	);
}
